/**
 * Track A · Phase A4 — pilot launch funnel.
 *
 * Renders the multi-stage acceptance funnel from
 * /api/admin/track-a/pilot-funnel. Pre-pilot, every stage is N/A so
 * operators can see the structure of the report before any subscriber
 * is in the cohort.
 *
 * The funnel is the master-plan decision gate for whether Track B
 * starts: low-cohort thresholds (e.g. activation ≥ 50% of clicks)
 * tell the team whether to continue, pivot, or stop.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, type ApiError } from '../api/client'
import { Panel } from '../components/Panel'

type Polarity = 'min' | 'max'

interface FunnelStage {
  name: string
  actual: number
  denom: number
  rate_pct: number | null
  threshold_pct: number
  polarity: Polarity
  denom_label: string
  note: string | null
  passing: boolean | null
}

interface FunnelSnapshot {
  publication_slug: string
  computed_at: string
  cohort_size: number
  stages: FunnelStage[]
}

const PUBS = [
  { slug: 'flight_journal',      label: 'Flight Journal' },
  { slug: 'diecast',             label: 'Die Cast X' },
  { slug: 'model_airplane_news', label: 'Model Airplane News' },
  { slug: 'rc_car_action',       label: 'RC Car Action' },
] as const

export function PilotFunnelPage() {
  const [pub, setPub] = useState<string>('flight_journal')
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['pilot-funnel', pub],
    queryFn: () =>
      api.get<FunnelSnapshot>(`/api/admin/track-a/pilot-funnel?pub=${pub}`),
    refetchOnWindowFocus: false,
  })

  return (
    <div className="p-8 space-y-6 max-w-[1100px]">
      <header className="border-b border-ink-500 pb-4 mb-2">
        <h1 className="font-serif text-[36px] leading-[1.05] tracking-tight text-paper">
          Track A · Pilot funnel
        </h1>
        <p className="text-paper-dim mt-2 text-[14px] max-w-[700px]">
          The master-plan acceptance gate for the FLJ pilot. Pre-launch
          every stage is N/A; once the onboarding-drip job ships its
          first batch the cohort populates and stages report against
          their thresholds. Use this to decide whether Track B starts.
        </p>
      </header>

      <div className="flex items-center gap-3">
        <span className="label text-paper-trace">Publication:</span>
        <select
          value={pub}
          onChange={(e) => setPub(e.target.value)}
          className="bg-ink-700 border border-ink-500 rounded px-2 py-1 font-mono text-[13px]"
        >
          {PUBS.map((p) => (
            <option key={p.slug} value={p.slug}>{p.label}</option>
          ))}
        </select>
        <button
          onClick={() => refetch()}
          className="px-3 py-1 border border-ink-500 rounded font-mono text-[12px]
                     text-paper-dim hover:text-amber-glow hover:border-amber-glow
                     transition-colors"
        >
          Refresh
        </button>
        {data && (
          <span className="font-mono text-[11px] text-paper-trace ml-auto">
            cohort size: <span className="text-paper">{data.cohort_size}</span>
            {' · '}
            computed {new Date(data.computed_at).toLocaleString()}
          </span>
        )}
      </div>

      {isLoading && (
        <Panel label="Loading…">
          <div className="px-4 py-6 label text-paper-trace">Loading…</div>
        </Panel>
      )}
      {error && (
        <Panel label="Error" accent="red">
          <div className="px-4 py-4 label text-radar-red">
            {(error as ApiError).message ?? 'failed'}
          </div>
        </Panel>
      )}

      {data && (
        <>
          {data.cohort_size === 0 && (
            <Panel label="Pre-pilot" accent="amber">
              <div className="px-4 py-4 text-paper-dim text-[13px]">
                Cohort is empty — no ``members_onboarding_sends`` rows
                with ``status='sent'`` for{' '}
                <span className="font-mono">{data.publication_slug}</span>{' '}
                yet. The funnel below shows the structure that will
                populate once the activation invite has shipped.
              </div>
            </Panel>
          )}

          <Panel label="Funnel" bodyClassName="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ink-500">
                  <Th>Stage</Th>
                  <Th align="right">Actual</Th>
                  <Th align="right">Denom</Th>
                  <Th align="right">Rate</Th>
                  <Th align="right">Threshold</Th>
                  <Th>Pass?</Th>
                </tr>
              </thead>
              <tbody>
                {data.stages.map((s, i) => (
                  <tr key={i} className="border-b border-ink-500/40">
                    <Td>
                      <div className="text-paper">{s.name}</div>
                      {s.note && (
                        <div className="text-[10px] text-paper-trace italic mt-0.5">
                          {s.note}
                        </div>
                      )}
                    </Td>
                    <Td align="right">
                      <span className="font-mono tabular-nums">{s.actual}</span>
                    </Td>
                    <Td align="right">
                      <span className="font-mono tabular-nums text-paper-dim">
                        {s.denom}
                      </span>
                      <span className="text-[10px] text-paper-trace ml-1">
                        {s.denom_label}
                      </span>
                    </Td>
                    <Td align="right">
                      <span className="font-mono tabular-nums">
                        {s.rate_pct === null ? '—' : `${s.rate_pct.toFixed(1)}%`}
                      </span>
                    </Td>
                    <Td align="right">
                      <span className="font-mono tabular-nums text-paper-dim">
                        {s.polarity === 'max' ? '≤ ' : '≥ '}
                        {s.threshold_pct.toFixed(1)}%
                      </span>
                    </Td>
                    <Td>
                      <PassPill passing={s.passing} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <Panel label="Master-plan kill criteria" accent="amber">
            <div className="px-4 py-4 text-paper-dim text-[13px] space-y-2">
              <p>
                Per the master plan, ANY of the following triggers a
                documented Track B reassessment (continue / pivot /
                stop), not an automatic continue:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-[12px]">
                <li>Claim/login completion below 40% of starts after one round of UX fixes</li>
                <li>Support tickets above 8 per 100 onboarded</li>
                <li>Login failure rate above 5% (excluding rate-limit blocks)</li>
                <li>Read-completion below 15%</li>
                <li>No second-session behavior after 30 days</li>
                <li>Material confusion about the digital-access value proposition</li>
                <li>Onboarding email engagement materially below industry baselines</li>
              </ul>
            </div>
          </Panel>
        </>
      )}
    </div>
  )
}

// ---- Local helpers --------------------------------------------------

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th className={`px-4 py-3 label text-paper-trace text-${align}`}>
      {children}
    </th>
  )
}
function Td({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <td className={`px-4 py-2 text-${align} text-paper-dim`}>{children}</td>
}
function PassPill({ passing }: { passing: boolean | null }) {
  if (passing === null) {
    return <span className="font-mono text-[11px] text-paper-trace">N/A</span>
  }
  if (passing) {
    return (
      <span className="font-mono text-[11px] text-radar-green">PASS</span>
    )
  }
  return (
    <span className="font-mono text-[11px] text-radar-red">FAIL</span>
  )
}
